import React, { FC, useContext } from 'react'
import { BlueConicConfigurationContext } from '../../configuration/context'
import { getEnumValues } from '@pushly/models'
import { Input, Select } from 'antd'
import { FreeTextInputFilter } from './blueconic-free-text-filter'
import { TaggedInputFilter } from './tagged-input-filter'
import { RelativeOrSpecificDateFilter } from './date-filter-builder'
import {
    CustomFilter,
    Filter,
    filterIsAnyOfOrAllOf,
    filterIsDateType,
    filterIsFreeText,
    FreeTextFilter,
    isAnyOrAllOfFilterBlueConicType,
    isCustomBlueConicType,
    isDateTypeBlueConicType,
    isFreeTextBlueConicType,
    MatchesAnyOfFilter,
    RelativeDateFilter,
} from '../types'
import { BlueConicFilterType, ConditionOperatorType, FilterType } from '../enums'
import { BLUECONIC_FILTER_DISPLAY_NAMES } from '../../constants'
import { alphaSort } from '../../../../../../../_utils/array'
import classnames from 'classnames'
import { EditableViewContext } from '@pushly/aqe/lib/components/hoc/editable-view/editable-view-context'

type MetaFilterBlueConicFilterType =
    | BlueConicFilterType.SITENAME
    | BlueConicFilterType.CATEGORY
    | BlueConicFilterType.KEYWORD
    | BlueConicFilterType.AUTHOR
    // | BlueConicFilterType.CUSTOM - not available v1
    | BlueConicFilterType.PROPERTY
    | BlueConicFilterType.PUBLICATION_DATE

type BlueConicFilterBuilderProps = {
    filter: Filter
    onChange: (value: Filter) => void
    isFirst: boolean
}

export const BlueConicFilterBuilder: FC<BlueConicFilterBuilderProps> = ({ filter, onChange, isFirst }) => {
    const viewCtx = useContext(EditableViewContext)
    const isReadonly = viewCtx.mode === 'view'
    const context = useContext(BlueConicConfigurationContext)

    const options = getEnumValues(BlueConicFilterType)
        .filter((t) => t !== BlueConicFilterType.PRODUCTS && t !== BlueConicFilterType.CUSTOM)
        .map((fType) => ({
            value: fType,
            label: BLUECONIC_FILTER_DISPLAY_NAMES[fType],
        }))
        .sort(({ label: aLabel }, { label: bLabel }) => alphaSort(aLabel, bLabel))

    const blueConicType = filter.blueConicType

    return (
        <div className={`blueconic-filter-builder ${isFirst ? 'first-row' : ''}`}>
            {!isFirst && (
                <div
                    className={`row-prefix ant-input-group-addon ant-input-group-compact-addon middle-size ${viewCtx.modeClassName}`}
                >
                    and
                </div>
            )}
            <Input.Group compact>
                <Select<MetaFilterBlueConicFilterType>
                    className={classnames(viewCtx.modeClassName, 'blueconic-filter-type-select')}
                    size="middle"
                    options={options}
                    value={blueConicType as MetaFilterBlueConicFilterType}
                    onChange={(v) => {
                        let nextFilter: Filter = { ...filter }
                        nextFilter.blueConicType = v

                        if (isAnyOrAllOfFilterBlueConicType(nextFilter.blueConicType)) {
                            nextFilter.filterType = FilterType.MATCHES_ANY_OF
                            ;(nextFilter as MatchesAnyOfFilter).value = undefined
                        } else if (isFreeTextBlueConicType(nextFilter.blueConicType)) {
                            ;(nextFilter as FreeTextFilter).filterType = FilterType.FREE_TEXT
                            ;(nextFilter as FreeTextFilter).value = undefined
                        } else if (isDateTypeBlueConicType(nextFilter.blueConicType)) {
                            nextFilter.filterType = FilterType.RELATIVE_DATE
                            ;(nextFilter as RelativeDateFilter).value = {
                                operator: ConditionOperatorType.LTE,
                                value: 7,
                                meta: {
                                    qualifier: 'days',
                                    timezone: context.domain.timezone,
                                },
                            }
                        } else if (isCustomBlueConicType(nextFilter.blueConicType)) {
                            nextFilter.filterType = FilterType.CUSTOM
                            ;(nextFilter as CustomFilter).nestedFilter = {
                                filterType: FilterType.FREE_TEXT,
                            }
                        }

                        onChange(nextFilter)
                    }}
                />

                {/* Not available V1 - determine included vs excluded filters*/}
                {/*{filterIsCustom(filter) && <CustomFilterBuilder filter={filter} onChange={onChange} />}*/}
                {filterIsFreeText(filter) && (
                    <FreeTextInputFilter filter={filter} onChange={(value) => onChange({ ...filter, value })} />
                )}
                {filterIsAnyOfOrAllOf(filter) && <TaggedInputFilter filter={filter} onChange={onChange} />}
                {filterIsDateType(filter) && <RelativeOrSpecificDateFilter filter={filter} onChange={onChange} />}
            </Input.Group>
        </div>
    )
}
